<template>
  <div class="owc-subnavigation-cta-buttons">
    <template v-if="isIconOnly && ctaIcon">
      <a
        :href="ctaIcon.url"
        target="_self"
        :aria-label="ctaIcon.label"
        :class="[
          'wb-button',
          'wb-button--primary',
          'wb-button--medium',
          'wb-button--icon-only',
          'owc-subnavigation__cta-button-primary',
          {
            'owc-subnavigation__cta-button-primary--visible': isConfigurationButtonVisible,
            'owc-subnavigation__cta-button-primary--active': isCtaButtonsTransitioned,
          },
        ]"
        @transitionend.stop=""
      >
        <wb-icon class="wb-button__icon" aria-hidden="true" :name="ctaIcon.icon"></wb-icon>
      </a>
    </template>
    <template v-else>
      <a
        v-for="cta in ctas"
        v-show="!!cta.label"
        :key="cta.label"
        :target="cta.target"
        :href="cta.url"
        @click="$emit('closeSecondLevel')"
        class="owc-subnavigation-cta-buttons__button wb-button"
        :class="[
          {
            'wb-button--theme-dark': isDarkMode,
            'wb-button--large': mobile,
            'wb-button--small': !mobile,
          },
          getCtaButtonClasses(cta),
        ]"
      >
        {{ cta.label }}
      </a>
    </template>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { initializeStore } from '@seamless/store';
import { connectionName, isWithinVehicleContext } from '@dh-io-globalelem/vehicle-data-store-connection';
import { ButtonType } from '@owc-types/Button.enum';
import { SubnavigationCtaTypes, SubnavigationCtaIcons } from './SubnavigationCtaButtons.enums';
import { LinkTarget } from '@owc-types/LinkTarget.enum';
import { BaseCtaLink } from '@owc-types/BaseCtaLink';
import {
  SubnavigationCtaButtonsComputed,
  SubnavigationCtaButtonsData,
  SubnavigationCtaButtonsMethods,
  SubnavigationCtaButtonsProps,
  SubnavigationCtaIconButton,
} from './SubnavigationCtaButtons.d';
import { setCtaButtonsFromService } from '@owc-utils/AutomatedCta/CtaService';
import { CtaButtonsDisplayMethod, ScenarioKeys } from '@owc-utils/AutomatedCta/CtaAutomation.enum';

/**
 * A CTA button mainly used in the Subnavigation component.
 * Provides the user with the possibility to do somethig based on a given context.
 */
export default Vue.extend<
  SubnavigationCtaButtonsData,
  SubnavigationCtaButtonsMethods,
  SubnavigationCtaButtonsComputed,
  SubnavigationCtaButtonsProps
>({
  name: 'owc-subnavigation-cta-buttons',
  props: {
    buttons: {
      type: Array,
      required: true,
    },
    ctaConfig: {
      type: Object,
      required: false,
    },
    isDarkMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    isIconOnly: {
      type: Boolean,
      default: false,
    },
    isConfigurationButtonVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCtaButtonsTransitioned: {
      type: Boolean,
      required: false,
      default: false,
    },
    vehicleData: {
      type: Object,
      required: false,
    },
  },
  computed: {
    isAutomatedDisplayMethod() {
      return this.ctaConfig?.ctaDisplayMethod === CtaButtonsDisplayMethod.Automate;
    },
  },
  data() {
    return {
      ctas: [],
      ctaIcon: undefined,
    };
  },
  async mounted() {
    this.$nextTick(() => {
      this.setCtaButtons();
    });
  },
  watch: {
    ctaConfig() {
      this.setCtaButtons();
    },
  },
  methods: {
    async setCtaButtons() {
      if (this.isAutomatedDisplayMethod) {
        if ((await isWithinVehicleContext()) && !this.vehicleData?.scoped) {
          this.setCtaButtonsFromStore();
        } else {
          const ctaData = await setCtaButtonsFromService(
            this.vehicleData,
            this.ctaConfig,
            this.ctaConfig?.scenario as ScenarioKeys,
          );
          if (ctaData?.primary) {
            this.ctas = this.mapToCtas(ctaData?.primary, ctaData?.secondary);
            this.ctaIcon = this.mapToCtaIcon(ctaData?.primary as SubnavigationCtaIconButton);
          }
        }
      } else {
        this.setCtaButtonsFromProps();
      }
    },
    setCtaButtonsFromStore() {
      const store = initializeStore();
      store.subscribe(connectionName, state => {
        this.$nextTick(() => {
          const { primary, secondary } = state.cta;
          this.ctas = this.mapToCtas(primary, secondary);
          this.ctaIcon = this.mapToCtaIcon(primary);
        });
      });
    },
    setCtaButtonsFromProps() {
      const linkItems = this.buttons.map(item => item.link);
      const primaryButton = linkItems.find(({ type }) => type === ButtonType.Primary);
      const secondaryButton = linkItems.find(({ type }) => type === ButtonType.Secondary) || undefined;
      if (secondaryButton) {
        // Due to workbench a primary button must to be on the right
        this.ctas = [secondaryButton, primaryButton].filter(cta => cta) as BaseCtaLink[];
      } else {
        this.ctas = [primaryButton].filter(cta => cta) as BaseCtaLink[];
      }
    },
    mapToCtas(primary, secondary) {
      const commonProps = {
        target: LinkTarget.Self,
      };

      const buttons = [
        {
          ...primary,
          ...commonProps,
          type: ButtonType.Primary,
        },
      ];

      if (secondary) {
        buttons.unshift({
          ...secondary,
          ...commonProps,
          type: ButtonType.Secondary,
        });
      }

      return buttons;
    },
    getCtaButtonClasses(cta) {
      if (cta.type === ButtonType.Primary) {
        return 'wb-button--primary';
      }
      return 'wb-button--secondary';
    },
    mapToCtaIcon(cta) {
      const commonProps = {
        target: LinkTarget.Self,
      };

      const iconButton = {
        ...cta,
        ...commonProps,
      };
      switch (iconButton.type) {
        case SubnavigationCtaTypes.Shop:
          iconButton.icon = SubnavigationCtaIcons.Shop;
          break;
        case SubnavigationCtaTypes.Configurator:
          iconButton.icon = SubnavigationCtaIcons.Configurator;
          break;
        default:
          iconButton.icon = SubnavigationCtaIcons.Enquiry;
          break;
      }

      return iconButton;
    },
  },
});
</script>
<style lang="scss">
@import '@owc-styles/utils/rtl-utils';

$SubnavigationCtaModuleName: 'owc-subnavigation-cta-buttons';

.#{$SubnavigationCtaModuleName} {
  @include breakpoint-from(mq5) {
    margin-left: var(--wb-spacing-xs);

    white-space: nowrap;
  }

  &__button {
    width: 100%;

    margin-bottom: var(--wb-spacing-xxs);

    white-space: nowrap;

    @include breakpoint-to(mq5) {
      margin-top: var(--wb-spacing-xxs);
    }
    @include breakpoint-from(mq5) {
      width: auto;

      margin-right: var(--wb-spacing-xxs);
      margin-bottom: 0;
    }
  }
}

@include rtl-rule('.owc-subnavigation-cta-buttons', false) {
  @include breakpoint-from(mq3) {
    &__button {
      margin-right: 0;
      margin-left: var(--wb-spacing-xxs);
    }
  }
}
</style>
